import { useQuery } from '@apollo/client';
import { Link } from 'react-router-dom';
import { GET_DASHBOARD_ACTIONS_QUERY, PENDING_ORDER_DATES_QUERY } from './queries';
import { Alert } from '../../components/primitives';
import {
  DashboardActions as DashboardActionsType,
  SellerOrder,
  SellerOrderConnection,
} from '@shopify-multiseller/graphql/partner/types';
import { acceptWindow } from '../../helpers/acceptWindow';
import { useSettings } from '../../hooks';

const plural = (n: number) => (n === 1 ? 'order' : 'orders');

/**
 * "Oldest expires in 9 hours." — the sentence the design puts under the count.
 *
 * A count of orders awaiting acceptance says something is waiting; it does not
 * say whether that means today or this week. The deadline is the reason to act
 * now, so it is the detail line rather than the generic reminder that one
 * exists. Undefined when the shop sets no accept window, or when every one of
 * them has already run out — "expired" is the order's news, not the queue's.
 */
const oldestClause = (orderDates: string[], windowHours: number): string | undefined => {
  const oldest = orderDates.filter(Boolean).sort()[0];
  if (!oldest) return undefined;
  const left = acceptWindow(oldest, windowHours);
  return left && !left.expired ? `Oldest expires in ${left.duration}.` : undefined;
};

/**
 * What is waiting on the seller, said once, at the top.
 *
 * These were cards with a count pill and a chevron, which made a queue of
 * three orders look like a navigation menu. They are alerts now: a line that
 * states the situation, a line of detail, and the one link that clears it.
 */
export const DashboardActions: React.FC = () => {
  // Derive everything from the query — no mirror state, no shared loading
  // context. `dashboardActions` stays populated from cache during refetches.
  const { data, error } = useQuery<{ dashboardActions: DashboardActionsType }>(
    GET_DASHBOARD_ACTIONS_QUERY,
    { fetchPolicy: 'cache-and-network' },
  );
  const { data: pendingData } = useQuery<{ sellerOrderConnection: SellerOrderConnection }>(
    PENDING_ORDER_DATES_QUERY,
    { fetchPolicy: 'cache-and-network' },
  );
  const acceptWindowHours = useSettings().sellerOrderAccept?.orderAcceptTimeout ?? 0;
  const dashboardActions = data?.dashboardActions;
  const hasNoActions =
    !dashboardActions?.ordersPendingApproval && !dashboardActions?.ordersPendingFulfillment;

  // Silent on failure: this is a shortcut to a queue that is also reachable
  // from Orders, and an error banner where a to-do list should be is worse
  // than no to-do list. Three sections each shouting their own failure turned
  // one bad response into a page of red.
  if (error) return null;
  if (!dashboardActions) return <Loader />;
  if (hasNoActions) return null;

  const pendingDates = (pendingData?.sellerOrderConnection.edges ?? [])
    .map((edge) => (edge?.node as SellerOrder | undefined)?.orderDate)
    .filter((date): date is string => !!date);
  // The clock if one can be stated, the reminder that there is one otherwise —
  // the alert must not go quiet while the dates are still in flight.
  const acceptanceDetail =
    oldestClause(pendingDates, acceptWindowHours) ?? 'Accept them before they time out.';

  return (
    <div className="mb-5 grid gap-3 sm:grid-cols-2">
      {dashboardActions.ordersPendingApproval > 0 && (
        <Alert
          variant="warning"
          data-testid="attention-pending-approval"
          title={`${dashboardActions.ordersPendingApproval} ${plural(
            dashboardActions.ordersPendingApproval,
          )} awaiting acceptance`}
          action={
            /* Straight into the queue: these need a decision each, which is
               what the triage view is for. */
            <Link to="/orders/triage" className="text-sm font-medium text-primary hover:underline">
              Review →
            </Link>
          }
        >
          {acceptanceDetail}
        </Alert>
      )}
      {dashboardActions.ordersPendingFulfillment > 0 && (
        <Alert
          variant="info"
          data-testid="attention-pending-fulfilment"
          title={`${dashboardActions.ordersPendingFulfillment} ${plural(
            dashboardActions.ordersPendingFulfillment,
          )} ready to ship`}
          action={
            <Link
              to="/orders/triage?queue=fulfil"
              className="text-sm font-medium text-primary hover:underline"
            >
              Fulfil →
            </Link>
          }
        >
          Accepted, waiting on fulfilment.
        </Alert>
      )}
    </div>
  );
};

const Loader: React.FC = () => (
  <div className="mb-5 grid gap-3 sm:grid-cols-2">
    {[0, 1].map((i) => (
      <div key={i} className="h-[68px] animate-pulse rounded-lg border border-border bg-card" />
    ))}
  </div>
);
